/* eslint-disable @typescript-eslint/no-empty-function */
import { createContext, useContext, useState } from 'react';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import updateContact, { UpdateContactFailure } from '../../../../domain/use_cases/contacts/updateContact';
import deleteContact from '../../../../domain/use_cases/contacts/deleteContact';
import AuthenticationContext from '../../auth/AuthenticationContext';
import { IContact } from '@leedpulse-monorepo/interfaces';

const ContactEditionFormContext = createContext({
  wipContact: null as IContact | null,
  updateStatus: ContextStatus.IDLE,
  deleteStatus: ContextStatus.IDLE,
  errorMessage: '',
  initWithContact: (_: IContact) => { },
  submitUpdate: () => { },
  submitDelete: () => { },
  onCivilityChange: (_: string) => { },
  onFirstnameChange: (_: string) => { },
  onLastnameChange: (_: string) => { },
  onPhoneChange: (_: string) => { },
  onEmailChange: (_: string) => { },
  onOwnerChange: (_: string) => { },
  onSenderChange: (_: string) => { },
  onDetailsChange: (_: string) => { },
  onProspectCategoryChange: (_: string) => { },
  onCompanyChange: (_: string) => { },
  onAdresseChange: (_: string) => { },
  onRoleChange: (_: string) => { },
  onWebsiteChange: (_: string) => { }
});

export const ContactEditionFormProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [wipContact, setWipContact] = useState(null as IContact | null);
  // Snapshot taken when the form opens: the update sends only what differs from it
  const [initialContact, setInitialContact] = useState(null as IContact | null);

  const [updateStatus, setUpdateStatus] = useState(ContextStatus.IDLE);
  const [deleteStatus, setDeleteStatus] = useState(ContextStatus.IDLE);
  const [errorMessage, setErrorMessage] = useState('');

  const authContext = useContext(AuthenticationContext);

  const initWithContact = async (subscription: IContact) => {
    // Copies, as the setters below mutate the nested contact map in place
    setWipContact(structuredClone(subscription));
    setInitialContact(structuredClone(subscription));
  };

  const submitUpdate = async () => {
    if (wipContact === null || initialContact === null || authContext.authentication === null) return;
    setUpdateStatus(ContextStatus.LOADING);
    try {
      await updateContact(initialContact, wipContact, authContext.authentication);
      setInitialContact(structuredClone(wipContact));
      setUpdateStatus(ContextStatus.SUCCESS);
    } catch (e) {
      if (e instanceof UpdateContactFailure) {
        setErrorMessage(e.message);
      }
      setUpdateStatus(ContextStatus.ERROR);
    }
  };

  const submitDelete = async () => {
    if (wipContact === null || authContext.authentication === null) return;
    setDeleteStatus(ContextStatus.LOADING);
    try {
      await deleteContact(wipContact, authContext.authentication);
      setDeleteStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setDeleteStatus(ContextStatus.ERROR);
    }
  };

  const onCivilityChange = (civility: string) => {
    setWipContact((o) => {
      if (o === null) return null;
      o.contact.civility = civility;
      return { ...o };
    });
  };

  const onFirstnameChange = (firstname: string) => {
    setWipContact(
      (o) => {
        if (o === null) return null;
        o.contact.firstname = firstname;
        return { ...o };
      }
    );
  };

  const onLastnameChange = (lastname: string) => {
    setWipContact((o) => {
      if (o === null) return null;
      o.contact.lastname = lastname;
      return { ...o };
    });
  };

  const onPhoneChange = (phone: string) => {
    setWipContact(
      (o) => {
        if (o === null) return null;
        o.contact.phone = phone;
        return { ...o };
      }
    );
  };

  const onEmailChange = (email: string) => {
    setWipContact(
      (o) => {
        if (o === null) return null;
        o.contact.email = email;
        return { ...o };
      }
    );
  };

  const onOwnerChange = (owner: string) => {
    setWipContact((o) => {
      if (o === null) return null;
      o.contact.owner = owner;
      return { ...o };
    });
  };

  const onSenderChange = (sender: string) => {
    setWipContact((o) => {
      if (o === null) return null;
      o.contact.sender = sender;
      return { ...o };
    });
  };

  const onDetailsChange = (details: string) => {
    setWipContact((o) => {
      if (o === null) return null;
      o.contact.details = details;
      return { ...o };
    });
  };

  const onProspectCategoryChange = (prospectCategory: string) => {
    setWipContact((o) => {
      if (o === null) return null;
      o.contact.prospect_category = prospectCategory;
      return { ...o };
    });
  };

  const onCompanyChange = (company: string) => {
    setWipContact((o) => {
      if (o === null) return null;
      o.contact.company = company;
      return { ...o };
    });
  };

  const onAdresseChange = (adresse: string) => {
    setWipContact((o) => {
      if (o === null) return null;
      o.contact.adresse = adresse;
      return { ...o };
    });
  };

  const onRoleChange = (role: string) => {
    setWipContact((o) => {
      if (o === null) return null;
      o.contact.role = role;
      return { ...o };
    });
  };

  const onWebsiteChange = (website: string) => {
    setWipContact((o) => {
      if (o === null) return null;
      o.contact.website = website;
      return { ...o };
    });
  };

  return (
    <ContactEditionFormContext.Provider
      value={{
        wipContact,
        updateStatus,
        deleteStatus,
        errorMessage,
        initWithContact,
        submitUpdate,
        submitDelete,
        onCivilityChange,
        onFirstnameChange,
        onLastnameChange,
        onPhoneChange,
        onEmailChange,
        onOwnerChange,
        onSenderChange,
        onDetailsChange,
        onProspectCategoryChange,
        onCompanyChange,
        onAdresseChange,
        onRoleChange,
        onWebsiteChange
      }}
    >
      {children}
    </ContactEditionFormContext.Provider>
  );
};

export default ContactEditionFormContext;
